/**
 * SalesRankingPopup — motywacyjny modal pokazywany po zalogowaniu wybranym pracownikom.
 * Wyświetla ranking średnich rachunków z ostatniego dnia + porównanie do średniej tygodnia.
 * Pokazuje się max 1 raz dziennie per user (localStorage).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PortalOverlay from '../common/PortalOverlay';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtPLN = (n) =>
  new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', maximumFractionDigits: 2 }).format(Number(n || 0));

const SalesRankingPopup = ({ user, location }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.id || !location) return undefined;

    let cancelled = false;
    const run = async () => {
      try {
        // Backend sam sprawdzi świeżą flagę (fallback gdy storage ma starą sesję bez notifications).
        // Wysyłamy user.id, żeby backend mógł zwrócić hasFlag.
        const r = await axios.get(`${API}/api/sales/ranking-popup`, {
          params: { location, user_id: user.id },
        });
        if (cancelled) return;
        const wants =
          (typeof r.data?.hasFlag === 'boolean' ? r.data.hasFlag : user?.notifications?.salesRankingPopup === true);
        if (!wants) return;
        if (!r.data?.hasData || !(r.data.ranking || []).length) return;
        setData(r.data);
        setOpen(true);
      } catch {
        /* cicho */
      }
    };
    run();
    return () => { cancelled = true; };
  }, [user?.id, user?.notifications?.salesRankingPopup, location]);

  if (!open || !data) return null;

  const ranking = data.ranking || [];
  const top = ranking[0];
  // Spróbuj namierzyć obecnego usera w rankingu (po imieniu lub nazwisku)
  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const myPos = userName
    ? ranking.findIndex((r) => (r.waiterName || '').toLowerCase().includes(userName.toLowerCase()))
    : -1;
  const me = myPos >= 0 ? ranking[myPos] : null;

  return (
    <PortalOverlay onClose={() => setOpen(false)}>
    <div
      data-testid="sales-ranking-popup"
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0, 0, 0, 0.78)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        animation: 'fadeIn 0.3s ease-out',
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 540, width: '100%',
          background: 'linear-gradient(150deg, #0f172a 0%, #1e293b 100%)',
          border: '1.5px solid rgba(212, 175, 55, 0.55)',
          borderRadius: 20, padding: 28,
          boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 0 6px rgba(212,175,55,0.12)',
          color: '#f8fafc', position: 'relative',
          maxHeight: '90vh', overflowY: 'auto',
        }}>
        {/* Close X */}
        <button
          onClick={() => setOpen(false)}
          data-testid="sales-ranking-popup-close"
          style={{
            position: 'absolute', top: 14, right: 14,
            width: 36, height: 36, borderRadius: 18,
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff', fontSize: 18, cursor: 'pointer',
          }}>✕</button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 40, marginBottom: 4 }}>🏆</div>
          <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 900, color: '#d4af37', letterSpacing: 0.4 }}>
            Ranking sprzedaży
          </h2>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>
            Średni rachunek z <b style={{ color: '#f8fafc' }}>{data.snapshotDate}</b>
          </div>
        </div>

        {/* Highlight top */}
        {top && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.04))',
            border: '1px solid rgba(212,175,55,0.4)',
            borderRadius: 14, padding: 14, marginBottom: 16, textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 2 }}>🥇</div>
            <div style={{ fontSize: 14, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Lider dnia
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#f8fafc', marginTop: 4 }}>
              {top.waiterName}
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#d4af37', marginTop: 4 }}>
              {fmtPLN(top.avgCheck)}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
              średni rachunek · {top.totalGuests} gości · {fmtPLN(top.totalRevenue)}
            </div>
          </div>
        )}

        {/* Full ranking */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            📊 Pełny ranking
          </div>
          {ranking.map((r, idx) => {
            const isMe = me && r.waiterName === me.waiterName;
            return (
              <div key={r.waiterName}
                style={{
                  display: 'grid', gridTemplateColumns: '36px 1fr auto', gap: 10, alignItems: 'center',
                  padding: '8px 10px', borderRadius: 8,
                  background: isMe ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${isMe ? 'rgba(212,175,55,0.5)' : 'rgba(255,255,255,0.06)'}`,
                  marginBottom: 4,
                }}>
                <div style={{ fontSize: 16, fontWeight: 700, textAlign: 'center', color: idx < 3 ? '#d4af37' : '#94a3b8' }}>
                  {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc' }}>
                  {r.waiterName} {isMe && <span style={{ fontSize: 10, marginLeft: 6, color: '#d4af37' }}>(TY)</span>}
                </div>
                <div style={{ fontSize: 14, fontWeight: 900, color: isMe ? '#d4af37' : '#f8fafc' }}>
                  {fmtPLN(r.avgCheck)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Personal stat */}
        {me ? (
          <div style={{
            background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.35)',
            borderRadius: 10, padding: 12, fontSize: 13, color: '#dcfce7', textAlign: 'center',
          }}>
            🎯 Twój wynik: <b style={{ color: '#22c55e' }}>{fmtPLN(me.avgCheck)}</b> — pozycja <b>#{myPos + 1}</b>
            {myPos === 0
              ? ' · Jesteś numerem 1! Tak trzymaj 🔥'
              : ` · do lidera brakuje ${fmtPLN(top.avgCheck - me.avgCheck)}`}
          </div>
        ) : (
          <div style={{
            background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.35)',
            borderRadius: 10, padding: 12, fontSize: 13, color: '#e0e7ff', textAlign: 'center',
          }}>
            💪 Pokaż wszystkim na co Cię stać — sprzedawaj więcej, polecaj dodatki, deser na koniec.
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button
            onClick={() => setOpen(false)}
            data-testid="sales-ranking-popup-ok"
            style={{
              padding: '12px 28px', borderRadius: 10, fontSize: 14, fontWeight: 800,
              background: 'linear-gradient(135deg, #d4af37, #c99e2f)',
              color: '#000', border: 'none', cursor: 'pointer',
              boxShadow: '0 6px 18px rgba(212,175,55,0.4)',
            }}>
            🚀 Lecimy z tym dniem!
          </button>
        </div>
      </div>
    </div>
    </PortalOverlay>
  );
};

export default SalesRankingPopup;
